import React, { useCallback, useState, useEffect, ReactNode } from 'react';
import { css, cx, ClassNamesArg } from '@emotion/css';
import { BlockButton } from '../internal';
import { teal } from '../colors';

export interface CarouselSlideProps {
  active?: boolean;
  index: number;
  animateOut?: boolean;
  animateIn?: boolean;
  direction?: string;
  onCarouselClick?: (index: number) => void;
  showZoomOverlay?: boolean;
  zoomOverlayContent?: ReactNode;
  children?: ReactNode;
}

const overlayCss = css`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100%;
  width: 100%;
  opacity: 0;
  transition: 0.5s ease;
  background-color: rgba(255, 255, 255, 0.8);
`;

const flexCenterCss = css`
  display: -ms-flexbox;
  display: flex;
  -ms-flex-pack: center;
  justify-content: center;
  -ms-flex-align: center;
  align-items: center;
`;

const itemCss = css`
  position: absolute;
  top: 0;
  opacity: 0;
  left: 0;
  right: 0;
  width: 100%;
  transition: 0.6s ease-in-out transform;
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  perspective: 1000px;
`;

const overlayContainerCss = css`
  &:hover {
    .crc-carousel-slide_overlay {
      opacity: 1;
    }
  }
`;

const activeCss = css`
  opacity: 1;
`;

const transitionLeft = css`
  right: 100%;
  transform: translateX(100%);
`;

const transitionRight = css`
  left: 100%;
  transform: translateX(-100%);
`;

const clickableCss = css`
  cursor: pointer;
  .crc-carousel-slide_overlay {
    cursor: pointer;
  }
`;

const CarouselSlide = ({
  active,
  animateIn,
  animateOut,
  direction,
  index,
  onCarouselClick,
  showZoomOverlay,
  zoomOverlayContent,
  ...props
}: CarouselSlideProps) => {
  const [currentDirection, setDirection] = useState<'left' | 'right' | null>(null);
  const [classes, setClasses] = useState({});

  const shouldRenderZoomOverlay = showZoomOverlay && !!onCarouselClick;

  const startAnimation = useCallback(() => {
    const newDirection = direction === 'prev' ? 'right' : 'left';
    setDirection(newDirection);
  }, [direction, setDirection]);

  useEffect(() => {
    const initialClasses: ClassNamesArg = {
      [itemCss]: true,
      [clickableCss]: !!onCarouselClick && active && !animateIn,
      [activeCss]: (active && !animateIn) || animateOut,
      [overlayContainerCss]: shouldRenderZoomOverlay,
    };
    if (direction && active && animateIn) {
      initialClasses[direction] = true;
      initialClasses[transitionLeft] = direction === 'next';
      initialClasses[transitionRight] = direction === 'prev';
    }

    if (currentDirection && (animateIn || animateOut)) {
      initialClasses.currentDirection = true;
    }
    setClasses(initialClasses);
  }, [onCarouselClick, active, animateIn, animateOut, shouldRenderZoomOverlay, direction, currentDirection]);

  useEffect(() => {
    setDirection(null);

    setTimeout(() => startAnimation(), 20);
  }, [active, startAnimation]);

  const onClick = () => {
    onCarouselClick && onCarouselClick(index);
  };

  const zoomContent = zoomOverlayContent || <span>Zoom In</span>;

  return (
    <BlockButton onClick={onClick} className={cx('crc-carousel-slide', classes)}>
      {props.children}
      {shouldRenderZoomOverlay ? (
        <div className={cx(overlayCss, flexCenterCss, 'crc-carousel-slide_overlay')} style={{ color: teal.base }}>
          {zoomContent}
        </div>
      ) : null}
    </BlockButton>
  );
};

export default CarouselSlide;
